// @ts-nocheck
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { PodStatus } from '@/components/pods/PodStatus';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { Id, Doc } from '@/convex/_generated/dataModel';
import { useSelectedPodStore } from '@/stores/selectedPodStore';
import { motion, AnimatePresence } from 'framer-motion';

export function PodHistory() {
	const location = useLocation();
	const selectedPodFromHistory = location.state?.selectedPod;
	const fromPath = location.state?.from || '/pods';
	const [activeTab, setActiveTab] = React.useState('twitter');
	const { user } = useAuth();
	const navigate = useNavigate();
	const { setSelectedPod } = useSelectedPodStore();
	const [expandedPods, setExpandedPods] = React.useState<Set<string>>(
		new Set()
	);

	const normalizeSocialPlatform = (platform?: string) => {
		const normalized = String(platform || '')
			.toLowerCase()
			.replace(/\s+/g, '');
		if (normalized === 'x' || normalized === 'twitterx') return 'twitter';
		return normalized;
	};

	// Query hooks
	const podHistory = useQuery(api.podHistory.list, {
		orderBy: 'desc',
	});
	const users = useQuery(api.users.list, {
		requestingUserId: user?.id?.toString() ?? '',
		apiKey: user?.initialApiKey ?? '',
	});
	const podUrls = useQuery(api.urls.list, { podId: null });
	const pods = useQuery(api.engagementPods.list, {
		userId: null,
		requestingUserId: (user?.id as unknown as string) ?? 'skip',
	});

	const createPodHistory = useMutation(api.podHistory.create);

	const getUserInfo = (userId: string) => {
		const user = users?.find((u: Doc<'users'>) => u._id === userId);
		return {
			email: user?.email || 'Unknown',
			name: user?.name || 'Unknown User',
		};
	};

	const getUrlsForHistory = (history: any) => {
		return (
			podUrls
				?.filter(
					(url: Doc<'urls'>) =>
						url.podId === history.podId && !url.isArchived
				)
				.map((url: Doc<'urls'>) => ({
					url: url.url,
					platform: url.platform,
				})) || []
		);
	};

	const renderPodHistory = (platform: string) => {
		const filteredHistory =
			podHistory?.filter(
				(history: { platform: string }) =>
					normalizeSocialPlatform(history.platform) ===
					normalizeSocialPlatform(platform)
			) || [];

		if (filteredHistory.length === 0) {
			return (
				<div className='mt-6 p-8 text-center border rounded-lg'>
					<p className='text-gray-500'>
						No history found for {platform} pods
					</p>
				</div>
			);
		}

		// Group history items by podName
		const groupedHistory = filteredHistory.reduce(
			(
				acc: Record<string, Doc<'podHistory'>[]>,
				history: Doc<'podHistory'>
			) => {
				if (!acc[history.podName]) {
					acc[history.podName] = [];
				}
				acc[history.podName].push(history);
				return acc;
			},
			{} as Record<string, typeof filteredHistory>
		);

		return (
			<div className='space-y-8 mt-6'>
				{Object.entries(groupedHistory).map(([podName, histories]) => (
					<Card key={podName} className='shadow-lg border-2'>
						<CardHeader>
							<div className='flex justify-between items-center'>
								<div className='flex items-center gap-2'>
									<motion.button
										onClick={() =>
											setExpandedPods((prev) => {
												const next = new Set(prev);
												if (next.has(podName)) {
													next.delete(podName);
												} else {
													next.add(podName);
												}
												return next;
											})
										}
										className='w-8 h-8 rounded-full bg-purple-900 flex items-center justify-center text-white hover:bg-purple-700 transition-colors'
										animate={{
											rotate: expandedPods.has(podName)
												? 180
												: 0,
										}}
										transition={{
											duration: 0.2,
											ease: 'easeInOut',
										}}
									>
										{expandedPods.has(podName)
											? '🔽'
											: '▶️'}
									</motion.button>
									<div>
										<CardTitle className='text-2xl font-bold text-purple-500'>
											{podName}
										</CardTitle>
										<p className='text-sm text-muted-foreground'>
											{
												(
													histories as Doc<'podHistory'>[]
												).length
											}{' '}
											historical events
										</p>
									</div>
								</div>
								<Button
									variant='outline'
									size='sm'
									onClick={() => {
										const podId = (
											histories as Doc<'podHistory'>[]
										)[0].podId;
										setSelectedPod(podId);
										navigate('/admin/pods', {
											state: { fromHistory: true },
										});
									}}
									className='bg-purple-600 hover:bg-purple-700 text-white font-bold px-6 py-2 text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 border-2 border-purple-400'
								>
									🎯 GO TO POD
								</Button>
							</div>
						</CardHeader>
						<AnimatePresence>
							{expandedPods.has(podName) && (
								<motion.div
									initial={{ opacity: 0, height: 0 }}
									animate={{ opacity: 1, height: 'auto' }}
									exit={{ opacity: 0, height: 0 }}
									transition={{ duration: 0.2 }}
								>
									<CardContent>
										<div className='space-y-4'>
											{(
												histories as Doc<'podHistory'>[]
											).map(
												(
													history: Doc<'podHistory'>
												) => (
													<Card
														key={history._id}
														className='border shadow'
													>
														<CardHeader>
															<div className='flex items-center justify-between'>
																<div className='space-y-2'>
																	<div className='flex items-center gap-2'>
																		<span className='text-sm text-muted-foreground'>
																			{format(
																				new Date(
																					history.timestamp
																				),
																				'MMM d, yyyy h:mm a'
																			)}
																		</span>
																		<span className='px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800'>
																			{
																				history.memberCount
																			}{' '}
																			Members
																		</span>
																		<span className='px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800'>
																			{
																				history.urlCount
																			}{' '}
																			URLs
																		</span>
																	</div>
																</div>
																<div className='flex flex-col items-end gap-2'>
																	<div className='text-right'>
																		<div className='font-semibold'>
																			Started
																			by{' '}
																			{
																				getUserInfo(
																					history.startedBy
																				)
																					.name
																			}
																		</div>
																		<div className='text-xs text-muted-foreground'>
																			{
																				getUserInfo(
																					history.startedBy
																				)
																					.email
																			}
																		</div>
																	</div>
																	<div className='text-right'>
																		<div className='font-semibold'>
																			Created
																			by{' '}
																			{
																				getUserInfo(
																					history.createdBy
																				)
																					.name
																			}
																		</div>
																		<div className='text-xs text-muted-foreground'>
																			{
																				getUserInfo(
																					history.createdBy
																				)
																					.email
																			}
																		</div>
																	</div>
																</div>
															</div>
														</CardHeader>
														<CardContent>
															<PodStatus
																status={
																	history.status
																}
																message={
																	history.statusMessage
																}
																timestamp={
																	history.timestamp
																}
																urlsRan={
																	history.urlCount
																}
																urls={getUrlsForHistory(
																	history
																)}
																runCount={
																	pods?.find(
																		(
																			p: Doc<'engagementPods'>
																		) =>
																			p._id ===
																			history.podId
																	)?.runCount
																}
															/>
														</CardContent>
													</Card>
												)
											)}
										</div>
									</CardContent>
								</motion.div>
							)}
						</AnimatePresence>
					</Card>
				))}
			</div>
		);
	};

	return (
		<div className='container mx-auto p-6 pb-32'>
			<div className='flex justify-between items-center mb-6'>
				<h1 className='text-3xl font-bold text-white'>Pod History</h1>
				<Button
					variant='outline'
					onClick={() => {
						setSelectedPod(null);
						navigate('/admin/pods', { replace: true });
					}}
					className='bg-purple-600 hover:bg-purple-700 text-white'
				>
					← Back to Pods
				</Button>
			</div>

			<Tabs
				defaultValue='twitter'
				className='space-y-4'
				onValueChange={setActiveTab}
			>
				<TabsList>
					<TabsTrigger value='twitter'>Twitter X</TabsTrigger>
					<TabsTrigger value='linkedin'>LinkedIn</TabsTrigger>
					<TabsTrigger value='facebook'>Facebook</TabsTrigger>
				</TabsList>

				<TabsContent value='twitter'>
					{renderPodHistory('twitter')}
				</TabsContent>

				<TabsContent value='linkedin'>
					{renderPodHistory('linkedin')}
				</TabsContent>

				<TabsContent value='facebook'>
					{renderPodHistory('facebook')}
				</TabsContent>
			</Tabs>
		</div>
	);
}
